import { APP_INITIALIZER, ApplicationConfig, LOCALE_ID, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, withComponentInputBinding, withNavigationErrorHandler } from '@angular/router';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import localeTh from '@angular/common/locales/th';
import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { responseAdapterInterceptor } from './core/interceptors/response-adapter.interceptor';
import { AuthService } from './core/services/auth.service';
import { AdminAuthService } from './core/services/admin-auth.service';
import { ThemeService } from './core/services/theme.service';

registerLocaleData(localeTh);

/**
 * หลัง deploy เวอร์ชันใหม่ ผู้ใช้ที่เปิดเว็บค้างไว้ยังถือ bundle เก่า → lazy route โหลด chunk ที่ไม่มีแล้ว
 * ("Failed to fetch dynamically imported module") → reload หน้าไปยัง URL ที่จะไป เพื่อรับ build ใหม่
 * กัน reload วนด้วย sessionStorage (reload ได้ 1 ครั้งต่อ 30 วินาที)
 */
function reloadOnStaleChunk(error: { error: unknown; url: string }): void {
  if (typeof window === 'undefined') return;
  const msg = String((error.error as Error)?.message ?? error.error ?? '');
  if (!/dynamically imported module|Importing a module script failed|Loading chunk|module script/i.test(msg)) return;
  const KEY = 'chunk-reload-at';
  try {
    const last = Number(sessionStorage.getItem(KEY) ?? 0);
    if (Date.now() - last < 30_000) return;
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch { /* storage ใช้ไม่ได้ → reload ครั้งเดียวตามปกติ */ }
  window.location.assign(error.url);
}

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: LOCALE_ID, useValue: 'th' },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding(), withNavigationErrorHandler(reloadOnStaleChunk)),
    provideClientHydration(withEventReplay()),
    provideHttpClient(
      withFetch(),
      withInterceptors([
        authInterceptor,
        responseAdapterInterceptor,
      ]),
    ),
    {
      provide: APP_INITIALIZER,
      useFactory: (theme: ThemeService) => () => theme.init(),
      deps: [ThemeService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (auth: AuthService) => () => auth.init(),
      deps: [AuthService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (admin: AdminAuthService) => () => admin.initAdmin(),
      deps: [AdminAuthService],
      multi: true,
    },
  ],
};
